import React from "react";
import { useAvailableProviders } from "../../../hooks/useAiAgents";
import { useTranslation } from "react-i18next";
import { useUiLocale } from "../../../hooks/useUiLocale";
import {
  resolveSchemaDescription,
  resolveSchemaTitle,
} from "../../../lib/aiAgentI18n";

const ProviderSchemasInfo = () => {
  const { t } = useTranslation("aiAgents");
  const { locale } = useUiLocale();
  const { data: availableProviders, isLoading } = useAvailableProviders();

  if (isLoading) {
    return (
      <div className="ordering-ai-agent-panel border-border bg-card rounded-lg border p-3 sm:p-6">
        <div className="animate-pulse space-y-3 sm:space-y-4">
          <div className="bg-card h-4 w-1/4 rounded sm:h-5"></div>
          <div className="space-y-2 sm:space-y-3">
            <div className="bg-card h-3 w-3/4 rounded sm:h-4"></div>
            <div className="bg-card h-3 w-1/2 rounded sm:h-4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!availableProviders || availableProviders.length === 0) {
    return (
      <div className="ordering-ai-agent-panel border-border bg-card rounded-lg border p-3 sm:p-6">
        <p className="text-muted-foreground text-sm sm:text-base">
          {t("schemas.empty")}
        </p>
      </div>
    );
  }

  return (
    <div className="ordering-ai-agent-panel border-border bg-card rounded-lg border p-3 sm:p-6">
      <h3 className="text-foreground mb-3 text-base font-semibold sm:mb-4 sm:text-lg">
        {t("schemas.title")}
      </h3>
      <div className="space-y-3 sm:space-y-4">
        {availableProviders.map((provider) => (
          <div
            key={provider.provider}
            className="border-border rounded-lg border p-3 sm:p-4"
          >
            <h4 className="sm:text-md text-foreground mb-2 text-sm font-medium capitalize sm:mb-3">
              {provider.provider}
            </h4>
            <div className="space-y-2 sm:space-y-3">
              {provider.types.map((type) => (
                <div
                  key={type.type}
                  className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between sm:gap-0"
                >
                  <div className="min-w-0 flex-1">
                    <span className="text-muted-foreground block text-xs sm:text-sm">
                      {resolveSchemaTitle({
                        title: type.title,
                        provider: provider.provider,
                        type: type.type,
                        t,
                        locale,
                      })}
                    </span>
                    {type.description && (
                      <p className="text-muted-foreground mt-1 line-clamp-2 text-xs">
                        {resolveSchemaDescription({
                          description: type.description,
                          provider: provider.provider,
                          type: type.type,
                          t,
                          locale,
                        })}
                      </p>
                    )}
                  </div>
                  <div className="flex items-center justify-end sm:justify-start">
                    {type.hasSettings ? (
                      <span className="border-success/30 text-success rounded-full border bg-[var(--success-bg)] px-2 py-1 text-xs whitespace-nowrap sm:px-3">
                        {t("schemas.badges.configurable")}
                      </span>
                    ) : (
                      <span className="border-border/30 bg-muted/20 text-muted-foreground rounded-full border px-2 py-1 text-xs whitespace-nowrap sm:px-3">
                        {t("schemas.badges.notAvailable")}
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ProviderSchemasInfo;
